/**
 * AnnouncementDetailPage — Full-page view of a single announcement.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Chip, Stack, Button,
  CircularProgress, Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PushPin as PinIcon,
  Campaign as AnnouncementIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { TYPE } from '@/theme/typography';
import { ANNOUNCEMENT_CATEGORY_COLORS } from '@/theme/colorTokens';
import apiClient from '@/api/client';
import { formatDate } from '@/utils/format';

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  general: { label: 'General', color: ANNOUNCEMENT_CATEGORY_COLORS.general },
  policy: { label: 'Policy', color: ANNOUNCEMENT_CATEGORY_COLORS.policy },
  event: { label: 'Event', color: ANNOUNCEMENT_CATEGORY_COLORS.event },
  hr: { label: 'HR', color: ANNOUNCEMENT_CATEGORY_COLORS.hr },
  safety: { label: 'Safety', color: ANNOUNCEMENT_CATEGORY_COLORS.safety },
  it: { label: 'IT', color: ANNOUNCEMENT_CATEGORY_COLORS.it },
};

const PRIORITY_COLORS: Record<string, 'default' | 'info' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'info',
  high: 'warning',
  urgent: 'error',
};

interface Announcement {
  id: string;
  title: string;
  body: string;
  category?: string;
  priority?: string;
  is_pinned: boolean;
  posted_by_name?: string;
  source?: string;
  created_at: string;
  updated_at: string;
}

export default function AnnouncementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery<Announcement>({
    queryKey: ['announcement', id],
    queryFn: () => apiClient.get(`/settings/announcements/${id}`).then((r) => r.data),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          Back
        </Button>
        <Typography color="error">Announcement not found.</Typography>
      </Box>
    );
  }

  const cat = CATEGORY_LABELS[data.category || 'general'] || CATEGORY_LABELS.general;

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2, fontSize: TYPE.sm }}>
        <MuiLink
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/portal')}
        >
          Portal
        </MuiLink>
        <Typography color="text.primary" sx={{ fontSize: TYPE.sm }}>
          Announcement
        </Typography>
      </Breadcrumbs>

      {/* Back button */}
      <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back
      </Button>

      <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <AnnouncementIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            {data.is_pinned && <PinIcon sx={{ fontSize: 16, color: 'primary.main' }} />}
            <Chip
              label={cat.label}
              size="small"
              variant="outlined"
              sx={{ height: 22, fontSize: TYPE.xs, borderColor: cat.color, color: cat.color }}
            />
            {data.priority && PRIORITY_COLORS[data.priority] && (
              <Chip
                label={data.priority.charAt(0).toUpperCase() + data.priority.slice(1)}
                size="small"
                color={PRIORITY_COLORS[data.priority]}
                variant="outlined"
                sx={{ height: 22, fontSize: TYPE.xs }}
              />
            )}
          </Stack>

          {/* Title */}
          <Typography variant="h5" fontWeight={TYPE.bold} sx={{ mb: 1.5 }}>
            {data.title}
          </Typography>

          {/* Meta */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            {data.posted_by_name && (
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                By {data.posted_by_name}
              </Typography>
            )}
            <Typography sx={{ fontSize: TYPE.sm, color: 'text.disabled' }}>
              {formatDate(data.created_at)}
            </Typography>
          </Stack>

          {/* Body */}
          <Box
            sx={{
              fontSize: TYPE.base,
              lineHeight: 1.8,
              color: 'text.primary',
              '& p': { mb: 1.5 },
              '& ul, & ol': { pl: 3, mb: 1.5 },
              '& a': { color: 'primary.main' },
              '& img': { maxWidth: '100%', borderRadius: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.body) }}
          />
        </CardContent>
      </Card>
    </Box>
  );
}
